/**
 * Music Platform Factory
 * Manages creation and switching between different music platform services
 */

import { 
  IMusicPlatformService, 
  IMusicPlatformFactory, 
  MusicPlatformType, 
  PlatformConfig 
} from '../types/MusicPlatform';

// Platform service imports
import { SpotifyService } from './SpotifyService';

class MusicPlatformFactory implements IMusicPlatformFactory {
  private static instance: MusicPlatformFactory;
  private currentService: IMusicPlatformService | null = null;
  private serviceCache = new Map<MusicPlatformType, IMusicPlatformService>();

  private constructor() {}

  static getInstance(): MusicPlatformFactory {
    if (!MusicPlatformFactory.instance) {
      MusicPlatformFactory.instance = new MusicPlatformFactory();
    }
    return MusicPlatformFactory.instance;
  }

  /**
   * Create or get cached service for a platform
   */
  createService(platform: MusicPlatformType, config: PlatformConfig): IMusicPlatformService {
    // Check cache first
    if (this.serviceCache.has(platform)) {
      return this.serviceCache.get(platform)!;
    }

    let service: IMusicPlatformService;

    switch (platform) {
      case 'spotify':
        service = SpotifyService.getInstance(config);
        break;
      case 'youtube':
        throw new Error('YouTube Music service not yet implemented');
      case 'tidal':
        throw new Error('Tidal service not yet implemented');
      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }

    // Cache the service
    this.serviceCache.set(platform, service);
    return service;
  }

  /**
   * Get all platforms that have service implementations
   */
  getAvailablePlatforms(): MusicPlatformType[] {
    return ['spotify'];
  }

  /**
   * Get platforms that the user is currently connected to
   */
  getConnectedPlatforms(): MusicPlatformType[] {
    const connected: MusicPlatformType[] = [];
    
    // Check Spotify
    if (localStorage.getItem('spotify_tokens')) {
      connected.push('spotify');
    }
    
    return connected;
  }

  /**
   * Get the currently active platform
   */
  getCurrentPlatform(): MusicPlatformType | null {
    const stored = localStorage.getItem('current_music_platform');
    if (stored && this.getAvailablePlatforms().includes(stored as MusicPlatformType)) {
      return stored as MusicPlatformType;
    }
    
    // Auto-detect if only one platform is connected
    const connected = this.getConnectedPlatforms();
    if (connected.length === 1) {
      this.setCurrentPlatform(connected[0]);
      return connected[0];
    }
    
    return null;
  }

  /**
   * Switch to a different platform
   */
  async switchPlatform(platform: MusicPlatformType): Promise<void> {
    if (!this.getAvailablePlatforms().includes(platform)) {
      throw new Error(`Platform ${platform} is not available`);
    }

    // Clear current service
    this.currentService = null;
    
    // Set new platform
    this.setCurrentPlatform(platform);
    
    console.log(`🔄 Switched to ${platform} music platform`);
  }

  /**
   * Get the current active service
   */
  getCurrentService(): IMusicPlatformService | null {
    if (this.currentService) {
      return this.currentService;
    }

    const currentPlatform = this.getCurrentPlatform();
    if (!currentPlatform) {
      return null;
    }

    try {
      // Get platform config
      const config = this.getPlatformConfig(currentPlatform);
      this.currentService = this.createService(currentPlatform, config);
      return this.currentService;
    } catch (error) {
      console.error(`Failed to create service for ${currentPlatform}:`, error);
      return null;
    }
  }

  /**
   * Clear all cached services (useful for logout)
   */
  clearServiceCache(): void {
    this.serviceCache.clear();
    this.currentService = null;
  }

  /**
   * Check if any platform is connected and ready
   */
  hasConnectedPlatform(): boolean {
    return this.getConnectedPlatforms().length > 0;
  }

  /**
   * Get status of all platforms
   */
  async getAllPlatformStatuses(): Promise<Record<MusicPlatformType, any>> {
    const statuses: Record<string, any> = {};
    
    for (const platform of this.getAvailablePlatforms()) {
      try {
        const config = this.getPlatformConfig(platform);
        const service = this.createService(platform, config);
        statuses[platform] = await service.getConnectionStatus();
      } catch (error) {
        statuses[platform] = {
          connected: false,
          authenticated: false,
          hasValidToken: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    }
    
    return statuses;
  }

  /**
   * Set current platform in localStorage
   */
  private setCurrentPlatform(platform: MusicPlatformType): void {
    localStorage.setItem('current_music_platform', platform);
  }

  /**
   * Get platform-specific configuration
   */
  private getPlatformConfig(platform: MusicPlatformType): PlatformConfig {
    switch (platform) {
      case 'spotify':
        return {
          clientId: process.env.REACT_APP_SPOTIFY_CLIENT_ID || '',
          redirectUri: process.env.REACT_APP_SPOTIFY_REDIRECT_URI || `${window.location.origin}/callback`,
          scopes: [
            'user-read-recently-played',
            'user-library-read',
            'playlist-modify-public',
            'playlist-modify-private',
            'user-read-private'
          ]
        };
      default:
        throw new Error(`No configuration available for platform: ${platform}`);
    }
  }
}

// Export singleton instance
export const musicPlatformFactory = MusicPlatformFactory.getInstance();
export { MusicPlatformFactory };